// src/utils/automationGraphMapper.ts

export interface FrontendNode {
    id: string;
    label: string;
    type?: string;
    trigger?: string;
    position: { x: number; y: number };
    tag?: string | string[];
    selectedTags?: string[];
    template?: string;
    variantId?: string;
    variables?: { name: string; address: string };
    delayType?: string;
    amount?: number;
    unit?: string;
    date?: string;
    time?: string;
    conditions?: any[];
    maxWaitAmount?: number;
    maxWaitUnit?: string;
    maxWaitDisplay?: string;
    buckets?: Array<{ name: string; weight: number; to?: string }>;
    isExit?: boolean;
    isBranch?: boolean;
    isMultiSplit?: boolean;
    multiSplitRules?: Array<{ id: string; condition: any; to: string; label: string }>;
    branchConditions?: string[];
    defaultTo?: string;
}

export interface FrontendEdge {
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
}

const TRIGGER_MAP: Record<string, string> = {
    FormSubmission: 'form_submission',
    NewSubscriber: 'new_subscriber',
    EmailOpened: 'email_opened',
    LinkClicked: 'link_clicked',
    ScheduledDateTime: 'scheduled_date_time',
};

const NODE_TYPE_MAP: Record<string, string> = {
    TAG_ADD: 'tags',
    TAG_REMOVE: 'tags',
    SEND_EMAIL: 'send',
    TIME_DELAY: 'time',
    WAIT_UNTIL: 'wait',
    EXIT: 'exit',
    BRANCH_TRUE_FALSE: 'branch',
    MULTI_SPLIT: 'multi',
    PERCENTAGE_SPLIT: 'percent',
};

/**
 * Maps API trigger conditions → frontend format
 * API:   { entity, field, operator }
 * Front: { category, operator, value }
 */
function mapTriggerConditions(apiConditions: any): {
    category: string;
    operator: string;
    value: string;
}[] {
    if (!apiConditions?.conditions || !Array.isArray(apiConditions.conditions)) {
        return [];
    }

    return apiConditions.conditions.map((c: any) => ({
        category: c.field || c.category || '',
        operator: c.operator || 'EXISTS',
        value: c.value || '',
    }));
}

/**
 * Format delay for display
 */
function formatDelay(delay: any): string {
    if (!delay) return 'Set Condition';

    if (typeof delay === 'object') {
        const parts: string[] = [];
        if (delay.days && delay.days > 0) {
            parts.push(`${delay.days} ${delay.days === 1 ? 'Day' : 'Days'}`);
        }
        if (delay.hours && delay.hours > 0) {
            parts.push(`${delay.hours} ${delay.hours === 1 ? 'Hour' : 'Hours'}`);
        }
        if (delay.minutes && delay.minutes > 0) {
            parts.push(`${delay.minutes} ${delay.minutes === 1 ? 'Minute' : 'Minutes'}`);
        }
        return parts.length > 0 ? parts.join(' ') : 'Set Condition';
    }

    if (typeof delay === 'string') {
        const m = delay.match(/(\d+)\s*(\w+)/);
        if (m) {
            const num = parseInt(m[1], 10);
            const unit = m[2].toLowerCase();
            const formattedUnit = unit.endsWith('s') ? unit : unit + 's';
            return `${num} ${num === 1 ? formattedUnit.slice(0, -1) : formattedUnit.charAt(0).toUpperCase() + formattedUnit.slice(1)}`;
        }
    }

    return 'Set Condition';
}

export function transformApiAutomationToFrontend(api: any) {
    const graph = api.graph ?? { nodes: {} };
    const apiNodes = graph.nodes ?? {};

    const nodes: FrontendNode[] = [];
    const edges: FrontendEdge[] = [];

    // Get the trigger type from the API
    const rawTrigger = api.trigger?.type ?? '';
    const triggerType = TRIGGER_MAP[rawTrigger] ?? rawTrigger.toLowerCase();

    // First pass: Create all nodes
    Object.values(apiNodes).forEach((apiNode: any) => {
        const id = apiNode.id;
        const apiType = apiNode.type as string;
        const cfg = apiNode.cfg ?? {};

        let label = 'Step';
        let type = NODE_TYPE_MAP[apiType] ?? apiType.toLowerCase();
        const extra: Partial<FrontendNode> = {};

        // Handle TRIGGER node - we'll map it to 'home'
        if (apiType === 'TRIGGER') {
            label = 'Trigger';
            type = 'trigger';
            extra.trigger = triggerType;
        } else if (apiType === 'TAG_ADD' || apiType === 'TAG_REMOVE') {
            label = 'Tags';
            const tags = Array.isArray(cfg.tags) ? cfg.tags : [];
            extra.tag = tags;
            extra.selectedTags = tags;
        } else if (apiType === 'SEND_EMAIL') {
            label = 'Send Email';
            const campaignId = cfg.campaignId || '';
            const variantId = cfg.variantId || campaignId;
            extra.template = campaignId;
            extra.variantId = variantId;
            extra.variables = cfg.variables || { name: '', address: '' };
        } else if (apiType === 'TIME_DELAY') {
            label = 'Time Delay';
            if (cfg.delay) {
                const m = String(cfg.delay).match(/(\d+)\s*(\w+)/);
                if (m) {
                    extra.delayType = 'fixed';
                    extra.amount = parseInt(m[1], 10);
                    const u = m[2].toLowerCase();
                    extra.unit = u.startsWith('day')
                        ? 'days'
                        : u.startsWith('week')
                            ? 'weeks'
                            : 'months';
                }
            } else {
                extra.delayType = cfg.delayType;
                extra.amount = cfg.amount;
                extra.unit = cfg.unit;
                extra.date = cfg.date;
                extra.time = cfg.time;
            }
        } else if (apiType === 'WAIT_UNTIL') {
            label = 'Wait Until';
            type = 'wait';

            // Parse the delay from cfg
            let delayDisplay = 'Set Condition';
            if (cfg.delay) {
                delayDisplay = formatDelay(cfg.delay);

                // Also set maxWaitAmount and maxWaitUnit for the node to display
                if (typeof cfg.delay === 'object') {
                    if (cfg.delay.days && cfg.delay.days > 0) {
                        extra.maxWaitAmount = cfg.delay.days;
                        extra.maxWaitUnit = 'days';
                    } else if (cfg.delay.hours && cfg.delay.hours > 0) {
                        extra.maxWaitAmount = cfg.delay.hours;
                        extra.maxWaitUnit = 'hours';
                    } else if (cfg.delay.minutes && cfg.delay.minutes > 0) {
                        extra.maxWaitAmount = cfg.delay.minutes;
                        extra.maxWaitUnit = 'minutes';
                    }
                } else if (typeof cfg.delay === 'string') {
                    const m = cfg.delay.match(/(\d+)\s*(\w+)/);
                    if (m) {
                        extra.maxWaitAmount = parseInt(m[1], 10);
                        const u = m[2].toLowerCase();
                        extra.maxWaitUnit = u.endsWith('s') ? u : u + 's';
                    }
                }
            }

            // Store the formatted delay for display
            extra.maxWaitDisplay = delayDisplay;
            extra.conditions = cfg.conditions ?? [];
        } else if (apiType === 'EXIT') {
            // Always label as just "Exit" regardless of ID
            label = 'Exit';
            type = 'exit';
            extra.isExit = true;
        } else if (apiType === 'BRANCH_TRUE_FALSE') {
            label = 'True/False Branch';
            type = 'branch';
            extra.isBranch = true;
            extra.branchConditions = cfg.condition?.conditions || [];
        } else if (apiType === 'MULTI_SPLIT') {
            label = 'Multi Split';
            type = 'multi';
            extra.isMultiSplit = true;

            // Build rules: First rule is Default, rest are conditions
            const rules = cfg.rules || [];
            const multiSplitRules: any[] = [];

            // Add Default rule first
            multiSplitRules.push({
                id: 'default',
                condition: '',
                to: cfg.defaultTo || '',
                label: 'Default',
            });

            // Add condition rules
            rules.forEach((rule: any, index: number) => {
                multiSplitRules.push({
                    id: rule.id || `rule-${index + 1}`,
                    condition: rule.condition || { entity: 'Email', field: '', operator: 'EXISTS', value: null },
                    to: rule.to || '',
                    label: rule.label || `Condition ${index + 1}`,
                });
            });

            extra.multiSplitRules = multiSplitRules;
            extra.defaultTo = cfg.defaultTo || '';
        } else if (apiType === 'PERCENTAGE_SPLIT') {
            label = 'Percentage Split';
            type = 'percent';
            const buckets = cfg.buckets || [
                { name: 'A', weight: 50 },
                { name: 'B', weight: 50 }
            ];
            extra.buckets = buckets.map((b: any, index: number) => ({
                name: b.name || String.fromCharCode(65 + index),
                weight: b.weight || 50,
                to: b.to || '',
            }));
        }

        // Only add non-TRIGGER nodes to the nodes array
        if (apiType !== 'TRIGGER') {
            nodes.push({
                id,
                label,
                type: id === 'home' ? 'trigger' : type,
                position: { x: 0, y: 0 },
                ...extra,
            });
        }
    });

    // Guarantee home node exists (this will be the trigger)
    const homeNode: FrontendNode = {
        id: 'home',
        label: 'Trigger',
        type: 'trigger',
        position: { x: 0, y: 0 },
        trigger: triggerType,
    };

    // Check if there's already a 'home' node, if not add it at the beginning
    const existingHomeIndex = nodes.findIndex((n) => n.id === 'home');
    if (existingHomeIndex === -1) {
        nodes.unshift(homeNode);
    } else {
        // Update existing home node with trigger info
        nodes[existingHomeIndex] = {
            ...nodes[existingHomeIndex],
            trigger: triggerType,
        };
    }

    // Second pass: Create all edges from all sources
    Object.values(apiNodes).forEach((apiNode: any) => {
        const sourceId = apiNode.id;
        const apiType = apiNode.type as string;
        const cfg = apiNode.cfg ?? {};

        // Determine the actual source ID for the edge
        // If source is TRIGGER, use 'home' instead
        const actualSource = apiType === 'TRIGGER' ? 'home' : sourceId;

        // 1. Handle regular next edges
        if (apiNode.edges?.next) {
            edges.push({
                id: `e${actualSource}-${apiNode.edges.next}`,
                source: actualSource,
                target: apiNode.edges.next,
            });
        }

        // 2. Handle branch true/false edges
        if (apiNode.edges?.true) {
            edges.push({
                id: `e${actualSource}-${apiNode.edges.true}`,
                source: actualSource,
                target: apiNode.edges.true,
                sourceHandle: 'true',
            });
        }
        if (apiNode.edges?.false) {
            edges.push({
                id: `e${actualSource}-${apiNode.edges.false}`,
                source: actualSource,
                target: apiNode.edges.false,
                sourceHandle: 'false',
            });
        }

        // 3. Handle PERCENTAGE_SPLIT edges from cfg.buckets
        if (apiType === 'PERCENTAGE_SPLIT' && cfg.buckets) {
            cfg.buckets.forEach((bucket: any, index: number) => {
                if (bucket.to) {
                    edges.push({
                        id: `e${actualSource}-${bucket.to}`,
                        source: actualSource,
                        target: bucket.to,
                        sourceHandle: `percent-${index}`,
                    });
                }
            });
        }

        // 4. Handle MULTI_SPLIT edges
        if (apiType === 'MULTI_SPLIT') {
            // Handle default edge
            if (cfg.defaultTo) {
                edges.push({
                    id: `e${actualSource}-${cfg.defaultTo}`,
                    source: actualSource,
                    target: cfg.defaultTo,
                    sourceHandle: 'default',
                });
            }

            // Handle rule edges
            if (cfg.rules) {
                cfg.rules.forEach((rule: any, index: number) => {
                    if (rule.to) {
                        const ruleId = rule.id || `rule-${index + 1}`;
                        edges.push({
                            id: `e${actualSource}-${rule.to}`,
                            source: actualSource,
                            target: rule.to,
                            sourceHandle: `rule-${ruleId}`,
                        });
                    }
                });
            }
        }
    });

    // Map trigger conditions
    const conditions = mapTriggerConditions(api.trigger?.conditions);

    const status =
        api.status === 'DRAFT' || api.draft
            ? 'draft'
            : api.status === 'ACTIVE' || api.active
                ? 'published'
                : 'draft';



    return {
        id: api.id,
        name: api.name ?? 'untitled',
        trigger: triggerType,
        status,
        nodes,
        edges,
        conditions,
        raw: api,
    };
}